import { useState } from "react";

type AsyncActionState<TResult> = {
  data: TResult | null;
  error: Error | null;
  pending: boolean;
};

function normalizeError(error: unknown) {
  if (error instanceof Error) return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return new Error(error.message);
  }
  return new Error("Something went wrong.");
}

export function useAsyncAction<TArgs extends unknown[], TResult>(
  action: (...args: TArgs) => Promise<TResult>,
) {
  const [state, setState] = useState<AsyncActionState<TResult>>({
    data: null,
    error: null,
    pending: false,
  });

  const run = async (...args: TArgs) => {
    setState((current) => ({ ...current, error: null, pending: true }));
    try {
      const data = await action(...args);
      setState({ data, error: null, pending: false });
      return data;
    } catch (error) {
      const normalized = normalizeError(error);
      setState((current) => ({
        ...current,
        error: normalized,
        pending: false,
      }));
      throw normalized;
    }
  };

  const reset = () => {
    setState({ data: null, error: null, pending: false });
  };

  return { ...state, reset, run };
}
